import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AutocompleteInputComponent } from "../../common/autocomplete-input/autocomplete-input.component";
import { TextEditorComponent } from "../../common/text-editor/text-editor.component";

@Component({
  selector: "app-add-missing-test-case-modal",
  standalone: true,
  imports: [CommonModule, FormsModule, AutocompleteInputComponent, TextEditorComponent],
  templateUrl: "./add-missing-test-case-modal.component.html",
  styleUrl: "./add-missing-test-case-modal.component.scss",
})
export class AddMissingTestCaseModalComponent implements OnInit, OnChanges {
  @Input()
  configs: any = {};
  isActive: boolean = false;

  userStory = "";
  description = "";
  testCaseId = "";
  storyId = "";
  testCaseType = "Smoke";
  subType = "";
  editingId: string | null = null;

  readonly testTypeOptions = ["Functional","Smoke", "Sanity", "Regression", "Non-functional"];

  label: string = 'Prerequisites';
  placeholder: any = 'Prerequisites...'
  labelSteps: string = 'Test Steps';
  placeholderSteps: any = 'Test Steps...'
  labelResult: string = 'Expected Result';
  placeholderResult: any = 'Expected Result...'

  textPrequities: string = '';
  textSteps: string = '';
  sanitizedTestSteps: string = '';
  textResult: string = '';
  isSubmitting = false;

  activeModal = inject(NgbActiveModal);

  /** When true (BPD suites), User Story ID and Story are not editable.
   *  Also true when editing a test case whose origin is not TCG_AI and
   *  both storyId and userStory are empty — those fields weren't populated
   *  for this record, so they should stay hidden and not be required. */
  get disableUserStoryFields(): boolean {
    if (!!this.configs?.data?.disableUserStoryFields) return true;

    if (this.editingId !== null) {
      const origin = this.configs?.data?.initialValues?.origin;
      if (origin !== 'TCG_AI' && !this.storyId && !this.userStory.trim()) {
        return true;
      }
    }

    return false;
  }

  get canSubmit(): boolean {
    const hasCoreFields =
      !!this.testCaseId.trim() &&
      !!this.description.trim() &&
      !!this.testCaseType.trim() &&
      !!this.subType.trim() &&
      !!this.textSteps.trim() &&
      !!this.textResult.trim();

    if (this.disableUserStoryFields) {
      return hasCoreFields;
    }
    return hasCoreFields && !!this.storyId && !!this.userStory.trim();
  }

  ngOnInit() {
    this.applyInitialValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["configs"]) {
      this.applyInitialValues();
    }
  }

  private applyInitialValues(): void {
    const initial = this.configs?.data?.initialValues;
    if (!initial) {
      this.testCaseType = "Smoke";
      this.subType = "";
      return;
    }

    this.editingId = initial._id ?? null;
    this.storyId = initial.storyId ?? "";
    this.userStory = initial.userStory ?? "";
    this.testCaseId = initial.testCaseId ?? "";
    this.description = initial.testCaseDescription ?? "";
    this.testCaseType = initial.testCaseType || initial.testType || "Smoke";
    this.subType = initial.subType || "";
    this.textPrequities = initial.prerequisites ?? "";
    this.textSteps = initial.testSteps ?? "";
    this.sanitizedTestSteps = initial.sanitizedTestSteps ?? "";
    this.textResult = initial.expectedResult ?? "";
  }

  add() {
    if (this.isSubmitting) {
      return;
    }

    const formData = {
      _id: this.editingId,
      testCaseId: this.testCaseId,
      storyId: this.storyId,
      testCaseDescription: this.description,
      userStory: this.userStory,
      testCaseType: this.testCaseType,
      subType: this.subType,
      prerequisites: this.textPrequities,
      testSteps: this.textSteps,
      sanitizedTestSteps: this.sanitizedTestSteps,
      expectedResult: this.textResult,
    };

    if (typeof this.configs?.onSubmit === 'function') {
      this.isSubmitting = true;
      this.configs.onSubmit(formData);
      return;
    }

    this.activeModal.close({ value: formData });
  }

  getStoryId(id: any) {
    this.storyId = id;
  }
}
